/**
 * 响应式配置Hook
 * Feature: mobile-responsive-framework, Task 2: 实现核心响应式Hooks
 * 需求: 1.1, 1.2, 9.3
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Grid } from 'antd';
import {
  BREAKPOINTS,
  BreakpointName,
  DeviceType,
  ScreenOrientation,
  getBreakpointName,
  getDeviceType,
  isMobileWidth,
  isTabletWidth,
  isDesktopWidth
} from '../constants/breakpoints';
import { isTouchDevice, isCoarsePointer } from '../constants/touch';

const { useBreakpoint } = Grid;

// 响应式配置接口
export interface ResponsiveConfig {
  // 设备类型
  isMobile: boolean;        // < 576px
  isTablet: boolean;        // 576px - 991px
  isDesktop: boolean;       // >= 992px
  isTouchDevice: boolean;   // 触摸设备检测
  
  // 屏幕信息
  screenWidth: number;
  screenHeight: number;
  orientation: ScreenOrientation;
  breakpoint: BreakpointName;
  deviceType: DeviceType;
  
  // 字体配置
  baseFontSize: number;     // 基础字体大小
  scaleFactor: number;      // 缩放因子
  
  // 布局配置
  containerPadding: number;
  gridGutter: number;
  formLayout: 'horizontal' | 'vertical';
  columns: number;          // 网格列数
  
  // Ant Design断点状态
  screens: ReturnType<typeof useBreakpoint>;
}

// 布局重计算防抖延迟
const RESIZE_DEBOUNCE_DELAY = 100;

/**
 * 响应式配置Hook
 * 提供设备类型检测、屏幕尺寸监听和布局配置计算
 */
export function useResponsiveConfig(): ResponsiveConfig {
  const screens = useBreakpoint();
  
  // 屏幕尺寸状态
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  });
  
  // 触摸设备检测
  const [touchDevice, setTouchDevice] = useState(false);
  
  // 初始化触摸设备检测
  useEffect(() => {
    setTouchDevice(isTouchDevice() || isCoarsePointer());
  }, []);
  
  // 防抖处理的resize事件
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setDimensions({
          width: window.innerWidth,
          height: window.innerHeight
        });
      }, RESIZE_DEBOUNCE_DELAY);
    };
    
    window.addEventListener('resize', handleResize);
    
    // 监听屏幕方向变化
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  
  // 计算设备类型
  const isMobile = useMemo(() => isMobileWidth(dimensions.width), [dimensions.width]);
  const isTablet = useMemo(() => isTabletWidth(dimensions.width), [dimensions.width]);
  const isDesktop = useMemo(() => isDesktopWidth(dimensions.width), [dimensions.width]);
  
  // 计算断点和设备类型
  const breakpoint = useMemo(() => getBreakpointName(dimensions.width), [dimensions.width]);
  const deviceType = useMemo(() => getDeviceType(dimensions.width), [dimensions.width]);
  
  // 计算屏幕方向
  const orientation: ScreenOrientation = useMemo(() => {
    return dimensions.width > dimensions.height ? 'landscape' : 'portrait';
  }, [dimensions.width, dimensions.height]);
  
  // 计算基础字体大小
  const baseFontSize = useMemo(() => {
    if (isMobile) return 14;
    if (isTablet) return 15;
    return 16;
  }, [isMobile, isTablet]);
  
  // 计算缩放因子
  const scaleFactor = useMemo(() => {
    const baseWidth = 1200;
    return Math.min(Math.max(dimensions.width / baseWidth, 0.75), 1.25);
  }, [dimensions.width]);
  
  // 计算容器内边距
  const containerPadding = useMemo(() => {
    if (isMobile) return 16;
    if (isTablet) return 24;
    return 32;
  }, [isMobile, isTablet]);
  
  // 计算网格间距
  const gridGutter = useMemo(() => {
    if (isMobile) return 12;
    if (isTablet) return 16;
    return 24;
  }, [isMobile, isTablet]);
  
  // 计算表单布局
  const formLayout = useMemo(() => {
    return isMobile ? 'vertical' : 'horizontal';
  }, [isMobile]);
  
  // 计算网格列数
  const columns = useMemo(() => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    return 4;
  }, [isMobile, isTablet]);
  
  return {
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice: touchDevice,
    screenWidth: dimensions.width,
    screenHeight: dimensions.height,
    orientation,
    breakpoint,
    deviceType,
    baseFontSize,
    scaleFactor,
    containerPadding,
    gridGutter,
    formLayout,
    columns,
    screens
  };
}

/**
 * 简化版响应式Hook，仅返回设备类型
 */
export function useDeviceType(): DeviceType {
  const { deviceType } = useResponsiveConfig();
  return deviceType;
}

/**
 * 简化版响应式Hook，仅返回是否为移动设备
 */
export function useIsMobile(): boolean {
  const { isMobile } = useResponsiveConfig();
  return isMobile;
}

/**
 * 简化版响应式Hook，仅返回当前断点
 */
export function useBreakpointName(): BreakpointName {
  const { breakpoint } = useResponsiveConfig();
  return breakpoint;
}

export default useResponsiveConfig;
